import React, { useState, useEffect } from 'react';
import {
  X, MapPin, Trash2, Edit2, Check, X as XIcon,
  Plus, ExternalLink, AlertCircle, History, Clock, FileDown
} from 'lucide-react';
import { jsPDF } from 'jspdf';
import { STATUS_COLORS, STATUS_TRANSITIONS } from '../utils/constants';
import { merchichConverter } from '../services/merchichService';
import {
  formatNumber, formatDate, formatDateTime, calculateResteAPayer,
  calculateTotalAvances, getZoneName, convertFirestoreTimestamp,
  normalizeClients, clientIdentifier
} from '../utils/helpers';
import { ProjectService, AgentService } from '../services/firebaseService';
import { AuditService, AUDIT_ACTIONS } from '../services/auditService';
import { NotifyService } from '../services/notifyService';
import { NotificationService } from '../services/notificationService';
import ProjectNotes from './ProjectNotes';
import {
  MODES_PAIEMENT, isProjectLate, getDaysSinceStatus, getPaymentStatus, PAYMENT_STATUS_LABELS, PAYMENT_STATUS_STYLES
} from '../utils/finance';

// Status Timeline Component
const StatusTimeline = ({ statusHistory = [], currentStatus }) => {
  // If no history, show current status only
  const history = statusHistory.length > 0 ? statusHistory : [
    { status: currentStatus, date: new Date().toISOString(), comment: 'Statut actuel' }
  ];

  return (
    <div className="relative">
      {history.map((entry, index) => {
        const statusColor = STATUS_COLORS[entry.status] || '#3B82F6';
        const isLast = index === history.length - 1;
        const date = new Date(entry.date);

        return (
          <div key={index} className="flex items-start mb-4 last:mb-0">
            {/* Timeline line */}
            <div className="flex flex-col items-center mr-4">
              <div
                className="w-4 h-4 rounded-full border-2 border-white shadow"
                style={{ backgroundColor: statusColor }}
              />
              {!isLast && (
                <div className="w-0.5 h-full min-h-[40px] bg-slate-200 mt-1" />
              )}
            </div>

            {/* Content */}
            <div className="flex-1 pb-2">
              <div className="flex items-center">
                <span
                  className="px-2 py-0.5 rounded-full text-white text-xs font-medium"
                  style={{ backgroundColor: statusColor }}
                >
                  {entry.status}
                </span>
                {entry.previousStatus && (
                  <span className="ml-2 text-xs text-slate-400">
                    (depuis {entry.previousStatus})
                  </span>
                )}
              </div>
              <div className="flex items-center mt-1 text-xs text-slate-500">
                <Clock className="h-3 w-3 mr-1" />
                {date.toLocaleDateString('fr-FR', {
                  day: 'numeric',
                  month: 'long',
                  year: 'numeric',
                  hour: '2-digit',
                  minute: '2-digit'
                })}
              </div>
              {entry.comment && (
                <p className="mt-1 text-sm text-slate-600">{entry.comment}</p>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
};

const ProjectDetails = ({
  project, onClose, onUpdate, onDelete, userId, readOnly = false, canSeeFinance = true,
  canManage = false, agents = [], author = null, delaiRetard = 15
}) => {
  const [editingField, setEditingField] = useState(null);
  const [editValue, setEditValue] = useState('');
  const [showAddAvance, setShowAddAvance] = useState(false);
  const [newAvance, setNewAvance] = useState({ montant: '', date: '', description: '', modePaiement: 'espece', enCaisse: true, reference: '' });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [statusComment, setStatusComment] = useState('');
  const [showStatusChange, setShowStatusChange] = useState(false);
  const [selectedNextStatus, setSelectedNextStatus] = useState('');
  const [userProfile, setUserProfile] = useState(null);
  const [generatingPdf, setGeneratingPdf] = useState(false);

  // Load user profile for receipt generation
  useEffect(() => {
    const loadProfile = async () => {
      if (userId) {
        const result = await AgentService.getUserData(userId);
        if (result.success && result.data?.profile) {
          setUserProfile(result.data.profile);
        }
      }
    };
    loadProfile();
  }, [userId]);

  if (!project) return null;

  const statut = project.statut || 'En cours';
  const statusColor = STATUS_COLORS[statut] || '#3B82F6';
  const availableTransitions = STATUS_TRANSITIONS[statut] || [];
  const totalAvances = calculateTotalAvances(project.avances);
  const resteAPayer = calculateResteAPayer(project.montantTotal, project.avances, project.fraisSupplementaires);
  const clients = normalizeClients(project);
  const representant = project.representant?.nom ? project.representant : null;

  // Changer le statut avec commentaire
  const handleStatusChange = async () => {
    if (!selectedNextStatus) return;
    if (selectedNextStatus === 'Défavorable' && !statusComment.trim()) {
      setError('Les remarques de la commission sont obligatoires pour un avis défavorable.');
      return;
    }
    setError('');
    setLoading(true);
    const result = await ProjectService.updateProjectStatus(project.id, selectedNextStatus, statusComment);
    if (result.success && onUpdate) {
      const newHistory = [...(project.statusHistory || []), {
        status: selectedNextStatus,
        date: new Date().toISOString(),
        previousStatus: statut,
        comment: statusComment
      }];
      onUpdate({ ...project, statut: selectedNextStatus, statusHistory: newHistory });
      AuditService.log(AUDIT_ACTIONS.PROJECT_STATUS, { entityType: 'dossier', entityId: project.id, label: `${project.refGlobal || ''} · ${statut} → ${selectedNextStatus}${statusComment ? ` (${statusComment})` : ''}` });
      NotifyService.send('statut', { dossier: project.refGlobal, client: project.client, ancien: statut, nouveau: selectedNextStatus, commentaire: statusComment, agent: project.agentResponsable, par: author?.name });
      if (selectedNextStatus === 'Favorable' || selectedNextStatus === 'Défavorable') NotificationService.avis(project, selectedNextStatus, statusComment.trim());
      setShowStatusChange(false);
      setStatusComment('');
      setSelectedNextStatus('');
    }
    setLoading(false);
  };

  // Édition inline
  const startEditing = (field, value) => {
    setEditingField(field);
    setEditValue(value || '');
  };

  const cancelEditing = () => {
    setEditingField(null);
    setEditValue('');
  };

  const saveField = async () => {
    if (!editingField) return;

    setLoading(true);
    const result = await ProjectService.updateProjectField(project.id, editingField, editValue);
    if (result.success && onUpdate) {
      onUpdate({ ...project, [editingField]: editValue });
    }
    setEditingField(null);
    setEditValue('');
    setLoading(false);
  };

  // Ajouter avance
  const handleAddAvance = async () => {
    if (!newAvance.montant || parseFloat(newAvance.montant) <= 0) {
      setError('Veuillez saisir un montant valide');
      return;
    }

    setLoading(true);
    setError('');

    const result = await ProjectService.addAvance(project.id, {
      montant: parseFloat(newAvance.montant),
      date: newAvance.date || new Date().toISOString().split('T')[0],
      description: newAvance.description,
      modePaiement: newAvance.modePaiement || 'espece',
      enCaisse: newAvance.enCaisse === true,
      reference: newAvance.reference || ''
    });

    if (result.success && onUpdate) {
      const updatedAvances = [...(project.avances || []), result.avance];
      onUpdate({ ...project, avances: updatedAvances });
      AuditService.log(AUDIT_ACTIONS.AVANCE_ADD, { entityType: 'dossier', entityId: project.id, label: `${project.refGlobal || ''} · ${formatNumber(newAvance.montant)} DH (${newAvance.modePaiement})` });
      NotifyService.send('avance', { dossier: project.refGlobal, client: project.client, montant: parseFloat(newAvance.montant), mode: newAvance.modePaiement, enCaisse: newAvance.enCaisse === true, par: author?.name });
      setNewAvance({ montant: '', date: '', description: '', modePaiement: 'espece', enCaisse: true, reference: '' });
      setShowAddAvance(false);
    } else {
      setError(result.error || 'Erreur lors de l\'ajout');
    }

    setLoading(false);
  };

  // Supprimer avance
  const handleDeleteAvance = async (avanceId) => {
    if (!window.confirm('Supprimer cette avance?')) return;

    setLoading(true);
    const removed = (project.avances || []).find(a => a.id === avanceId);
    const result = await ProjectService.deleteAvance(project.id, avanceId);
    if (result.success && onUpdate) {
      const updatedAvances = (project.avances || []).filter(a => a.id !== avanceId);
      onUpdate({ ...project, avances: updatedAvances });
      AuditService.log(AUDIT_ACTIONS.AVANCE_DELETE, { entityType: 'dossier', entityId: project.id, label: `${project.refGlobal || ''} · ${formatNumber(removed?.montant || 0)} DH` });
    }
    setLoading(false);
  };

  // Supprimer projet
  const handleDelete = async () => {
    if (!window.confirm('Êtes-vous sûr de vouloir supprimer ce projet? Cette action est irréversible.')) return;
    AuditService.log(AUDIT_ACTIONS.PROJECT_DELETE, { entityType: 'dossier', entityId: project.id, label: `${project.refGlobal || ''} · ${project.client || ''}` });

    setLoading(true);
    const result = await ProjectService.deleteProject(project.id);
    if (result.success && onDelete) {
      onDelete(project.id);
    }
    setLoading(false);
  };

  // Ouvrir dans Google Maps
  const openInGoogleMaps = () => {
    if (!project.coordonnees || project.coordonnees.length === 0) return;

    const firstPoint = project.coordonnees[0];
    const wgs84 = merchichConverter.merchichToWGS84(firstPoint.x, firstPoint.y, project.zoneMerchich);

    if (wgs84) {
      window.open(`https://maps.google.com/maps?q=${wgs84.latitude},${wgs84.longitude}&hl=fr&z=15`, '_blank');
    }
  };

  // Convert number to words in French
  const numberToWordsFr = (num) => {
    const units = ['', 'un', 'deux', 'trois', 'quatre', 'cinq', 'six', 'sept', 'huit', 'neuf', 'dix',
      'onze', 'douze', 'treize', 'quatorze', 'quinze', 'seize', 'dix-sept', 'dix-huit', 'dix-neuf'];
    const tens = ['', '', 'vingt', 'trente', 'quarante', 'cinquante', 'soixante', 'soixante', 'quatre-vingt', 'quatre-vingt'];

    if (num === 0) return 'zero';
    if (num < 0) return 'moins ' + numberToWordsFr(-num);

    let words = '';

    if (Math.floor(num / 1000000) > 0) {
      words += (Math.floor(num / 1000000) === 1 ? 'un million ' : numberToWordsFr(Math.floor(num / 1000000)) + ' millions ');
      num %= 1000000;
    }

    if (Math.floor(num / 1000) > 0) {
      words += (Math.floor(num / 1000) === 1 ? 'mille ' : numberToWordsFr(Math.floor(num / 1000)) + ' mille ');
      num %= 1000;
    }

    if (Math.floor(num / 100) > 0) {
      words += (Math.floor(num / 100) === 1 ? 'cent ' : numberToWordsFr(Math.floor(num / 100)) + ' cent ');
      num %= 100;
    }

    if (num > 0) {
      if (num < 20) {
        words += units[num];
      } else {
        const t = Math.floor(num / 10);
        const u = num % 10;
        if (t === 7 || t === 9) {
          words += tens[t] + '-' + units[10 + u];
        } else if (t === 8 && u === 0) {
          words += 'quatre-vingts';
        } else {
          words += tens[t] + (u > 0 ? (u === 1 && t !== 8 ? '-et-un' : '-' + units[u]) : '');
        }
      }
    }

    return words.trim();
  };

  // Generate PDF Receipt
  const generateReceipt = async () => {
    setGeneratingPdf(true);

    try {
      const doc = new jsPDF({
        orientation: 'portrait',
        unit: 'mm',
        format: 'a4'
      });

      // Simple number formatter for PDF (avoid special characters)
      const formatPdfNumber = (num) => {
        if (num === null || num === undefined) return '0';
        return Math.round(num).toString().replace(/\B(?=(\d{3})+(?!\d))/g, ' ');
      };

      const pageWidth = doc.internal.pageSize.getWidth();
      const margin = 20;
      const contentWidth = pageWidth - 2 * margin;
      const mainColor = userProfile?.logoColor || '#3B82F6';

      // Convert hex to RGB
      const hexToRgb = (hex) => {
        const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
        return result ? {
          r: parseInt(result[1], 16),
          g: parseInt(result[2], 16),
          b: parseInt(result[3], 16)
        } : { r: 59, g: 130, b: 246 };
      };

      const rgb = hexToRgb(mainColor);
      let yPos = 20;

      // Header background
      doc.setFillColor(rgb.r, rgb.g, rgb.b);
      doc.rect(0, 0, pageWidth, 45, 'F');

      // Logo (if exists)
      if (userProfile?.logoUrl) {
        try {
          doc.addImage(userProfile.logoUrl, 'PNG', margin, 8, 30, 30);
        } catch (e) {
          // Skip logo if it fails
        }
      }

      // Company name
      doc.setTextColor(255, 255, 255);
      doc.setFontSize(18);
      doc.setFont('helvetica', 'bold');
      const companyName = userProfile?.nomSociete || userProfile?.nomArchitecte || 'Cabinet d\'Architecture';
      doc.text(companyName, userProfile?.logoUrl ? margin + 35 : margin, 22);

      // Architect name (if different from company)
      if (userProfile?.nomSociete && userProfile?.nomArchitecte) {
        doc.setFontSize(11);
        doc.setFont('helvetica', 'normal');
        doc.text(userProfile.nomArchitecte, userProfile?.logoUrl ? margin + 35 : margin, 30);
      }

      // Receipt title
      doc.setFontSize(14);
      doc.setFont('helvetica', 'bold');
      doc.text('RECU DE PAIEMENT', pageWidth - margin - 50, 25);

      yPos = 55;

      // Receipt number and date
      doc.setTextColor(rgb.r, rgb.g, rgb.b);
      doc.setFontSize(10);
      doc.setFont('helvetica', 'bold');
      doc.text(`N° ${project.refGlobal || 'N/A'}`, pageWidth - margin - 40, yPos);

      doc.setTextColor(100, 100, 100);
      doc.setFont('helvetica', 'normal');
      doc.text(`Date: ${new Date().toLocaleDateString('fr-FR')}`, pageWidth - margin - 40, yPos + 6);

      // Client section
      doc.setFillColor(245, 245, 245);
      doc.roundedRect(margin, yPos + 15, contentWidth, 30, 3, 3, 'F');

      doc.setTextColor(rgb.r, rgb.g, rgb.b);
      doc.setFontSize(10);
      doc.setFont('helvetica', 'bold');
      doc.text('CLIENT', margin + 5, yPos + 25);

      doc.setTextColor(50, 50, 50);
      doc.setFontSize(12);
      const namedClients = clients.filter(c => c.nom);
      if (namedClients.length === 0) {
        doc.text('Non spécifié', margin + 5, yPos + 35);
      }
      let clientY = yPos + 35;
      namedClients.forEach((c) => {
        doc.setFontSize(12);
        doc.setTextColor(50, 50, 50);
        doc.text(c.nom, margin + 5, clientY);
        const ident = clientIdentifier(c);
        if (ident) {
          doc.setFontSize(9);
          doc.setTextColor(100, 100, 100);
          doc.text(ident, margin + 80, clientY);
        }
        clientY += 6;
      });

      doc.setFontSize(9);
      doc.setTextColor(100, 100, 100);
      if (representant) {
        doc.text(`Représenté par: ${representant.nom}${representant.qualite ? ` (${representant.qualite})` : ''}${representant.cin ? ` - CIN ${representant.cin}` : ''}`, margin + 5, clientY + 1);
        clientY += 6;
      }
      if (project.commune) {
        doc.text(`Commune: ${project.commune}`, margin + 5, clientY + 1);
        clientY += 6;
      }

      yPos = clientY + 14;

      // Project reference
      doc.setFillColor(rgb.r, rgb.g, rgb.b);
      doc.setDrawColor(rgb.r, rgb.g, rgb.b);
      doc.roundedRect(margin, yPos, contentWidth, 12, 2, 2, 'F');
      doc.setTextColor(255, 255, 255);
      doc.setFontSize(10);
      doc.setFont('helvetica', 'bold');
      doc.text('DESIGNATION DU PROJET', margin + 5, yPos + 8);

      yPos += 17;

      doc.setTextColor(50, 50, 50);
      doc.setFontSize(10);
      doc.setFont('helvetica', 'normal');
      doc.text(`Projet: ${project.natureProjet || 'Non spécifié'}`, margin + 5, yPos);
      doc.text(`Référence: ${project.refGlobal || '-'}`, pageWidth - margin - 50, yPos);

      yPos += 15;

      // Financial details table header
      doc.setFillColor(rgb.r, rgb.g, rgb.b);
      doc.roundedRect(margin, yPos, contentWidth, 10, 2, 2, 'F');
      doc.setTextColor(255, 255, 255);
      doc.setFontSize(9);
      doc.setFont('helvetica', 'bold');
      doc.text('DESCRIPTION', margin + 5, yPos + 7);
      doc.text('MONTANT (DH)', pageWidth - margin - 35, yPos + 7);

      yPos += 15;

      // Financial rows
      const rows = [
        { label: 'Montant du projet (Honoraires)', value: project.montantTotal || 0 },
        { label: `Frais supplémentaires${project.fraisSupplementairesNote ? ` (${project.fraisSupplementairesNote})` : ''}`, value: project.fraisSupplementaires || 0 }
      ];

      doc.setTextColor(50, 50, 50);
      doc.setFont('helvetica', 'normal');

      rows.forEach((row) => {
        doc.setFillColor(250, 250, 250);
        doc.rect(margin, yPos - 5, contentWidth, 10, 'F');
        doc.text(row.label, margin + 5, yPos);
        doc.text(formatPdfNumber(row.value) + ' DH', pageWidth - margin - 35, yPos);
        yPos += 12;
      });

      // Total project
      const totalProjet = (project.montantTotal || 0) + (project.fraisSupplementaires || 0);
      doc.setFillColor(rgb.r, rgb.g, rgb.b);
      doc.setDrawColor(rgb.r, rgb.g, rgb.b);
      doc.rect(margin, yPos - 5, contentWidth, 10, 'F');
      doc.setTextColor(255, 255, 255);
      doc.setFont('helvetica', 'bold');
      doc.text('TOTAL PROJET', margin + 5, yPos);
      doc.text(formatPdfNumber(totalProjet) + ' DH', pageWidth - margin - 35, yPos);

      yPos += 20;

      // Avances section
      if (project.avances && project.avances.length > 0) {
        doc.setTextColor(rgb.r, rgb.g, rgb.b);
        doc.setFontSize(10);
        doc.setFont('helvetica', 'bold');
        doc.text('AVANCES RECUES', margin + 5, yPos);
        yPos += 8;

        doc.setTextColor(50, 50, 50);
        doc.setFontSize(9);
        doc.setFont('helvetica', 'normal');

        project.avances.forEach((avance, index) => {
          doc.setFillColor(index % 2 === 0 ? 250 : 255, index % 2 === 0 ? 250 : 255, index % 2 === 0 ? 250 : 255);
          doc.rect(margin, yPos - 4, contentWidth, 8, 'F');
          doc.text(`Avance ${index + 1} - ${formatDate(avance.date)}`, margin + 5, yPos);
          doc.text(formatPdfNumber(avance.montant) + ' DH', pageWidth - margin - 35, yPos);
          yPos += 10;
        });

        // Total avances
        doc.setFillColor(200, 230, 200);
        doc.rect(margin, yPos - 4, contentWidth, 10, 'F');
        doc.setTextColor(34, 139, 34);
        doc.setFont('helvetica', 'bold');
        doc.text('TOTAL AVANCES', margin + 5, yPos + 2);
        doc.text(formatPdfNumber(totalAvances) + ' DH', pageWidth - margin - 35, yPos + 2);

        yPos += 18;
      }

      // Reste à payer
      doc.setFillColor(resteAPayer > 0 ? 255 : 200, resteAPayer > 0 ? 235 : 230, resteAPayer > 0 ? 220 : 200);
      doc.roundedRect(margin, yPos, contentWidth, 18, 3, 3, 'F');
      doc.setTextColor(resteAPayer > 0 ? 200 : 34, resteAPayer > 0 ? 100 : 139, resteAPayer > 0 ? 0 : 34);
      doc.setFontSize(12);
      doc.setFont('helvetica', 'bold');
      doc.text('RESTE A PAYER', margin + 5, yPos + 12);
      doc.text(formatPdfNumber(resteAPayer) + ' DH', pageWidth - margin - 40, yPos + 12);

      yPos += 25;

      // Amount in words
      if (totalAvances > 0) {
        doc.setTextColor(80, 80, 80);
        doc.setFontSize(9);
        doc.setFont('helvetica', 'italic');
        doc.text(`Arrêté le présent reçu à la somme de: ${numberToWordsFr(Math.floor(totalAvances))} dirhams`, margin, yPos);
      }

      yPos += 20;

      // Footer - Company info
      doc.setDrawColor(200, 200, 200);
      doc.line(margin, yPos, pageWidth - margin, yPos);
      yPos += 8;

      doc.setTextColor(100, 100, 100);
      doc.setFontSize(8);
      doc.setFont('helvetica', 'normal');

      const infoLines = [];
      if (userProfile?.siegeSocial) infoLines.push(userProfile.siegeSocial);

      const contactInfo = [];
      if (userProfile?.mobile) contactInfo.push(`Tél: ${userProfile.mobile}`);
      if (userProfile?.fixe) contactInfo.push(`Fixe: ${userProfile.fixe}`);
      if (userProfile?.email) contactInfo.push(`Email: ${userProfile.email}`);
      if (contactInfo.length) infoLines.push(contactInfo.join(' | '));

      const fiscalInfo = [];
      if (userProfile?.ice) fiscalInfo.push(`ICE: ${userProfile.ice}`);
      if (userProfile?.patente) fiscalInfo.push(`Patente: ${userProfile.patente}`);
      if (userProfile?.rc) fiscalInfo.push(`RC: ${userProfile.rc}`);
      if (userProfile?.identifiantFiscal) fiscalInfo.push(`IF: ${userProfile.identifiantFiscal}`);
      if (fiscalInfo.length) infoLines.push(fiscalInfo.join(' | '));

      // Bank info - show if either RIB or bank is filled
      const bankInfo = [];
      if (userProfile?.rib) bankInfo.push(`RIB: ${userProfile.rib}`);
      if (userProfile?.banque) bankInfo.push(userProfile.banque);
      if (bankInfo.length) infoLines.push(bankInfo.join(' - '));

      infoLines.forEach((line) => {
        doc.text(line, pageWidth / 2, yPos, { align: 'center' });
        yPos += 5;
      });

      // Signature area
      yPos = Math.max(yPos + 10, 250);
      doc.setTextColor(50, 50, 50);
      doc.setFontSize(9);
      doc.text('Signature et cachet:', pageWidth - margin - 50, yPos);
      doc.setDrawColor(200, 200, 200);
      doc.rect(pageWidth - margin - 60, yPos + 5, 50, 25);

      // Save PDF
      const fileName = `Recu_${project.refGlobal || 'projet'}_${new Date().toISOString().split('T')[0]}.pdf`;
      doc.save(fileName);

    } catch (err) {
      console.error('Error generating PDF:', err);
      setError('Erreur lors de la génération du reçu');
    }

    setGeneratingPdf(false);
  };

  // Composant champ éditable
  const EditableField = ({ label, field, value, type = 'text' }) => (
    <div className="group">
      <p className="text-xs text-slate-500 mb-1">{label}</p>
      {editingField === field ? (
        <div className="flex items-center space-x-2">
          <input
            type={type}
            value={editValue}
            onChange={(e) => setEditValue(e.target.value)}
            className="flex-1 px-2 py-1 border border-brand-300 rounded text-sm focus:ring-2 focus:ring-brand-500"
            autoFocus
          />
          <button
            onClick={saveField}
            disabled={loading}
            className="icon-btn !w-9 !h-9 text-emerald-600 hover:bg-emerald-50" aria-label="Valider"
          >
            <Check className="h-4 w-4" />
          </button>
          <button
            onClick={cancelEditing}
            className="icon-btn !w-9 !h-9" aria-label="Annuler"
          >
            <XIcon className="h-4 w-4" />
          </button>
        </div>
      ) : (
        <div className="flex items-center justify-between">
          <p className="font-medium text-slate-900">{value || '-'}</p>
          {!readOnly && (
            <button
              onClick={() => startEditing(field, value)}
              className="icon-btn !w-9 !h-9 text-slate-400 sm:opacity-0 sm:group-hover:opacity-100 focus:opacity-100 hover:text-brand-700 hover:bg-brand-50" aria-label="Modifier"
            >
              <Edit2 className="h-3 w-3" />
            </button>
          )}
        </div>
      )}
    </div>
  );

  return (
    <div className="modal-scrim" style={{ zIndex: 9999 }}>
      <div className="modal-panel max-w-4xl">
        {/* Header */}
        <div className="flex items-center justify-between px-4 sm:px-6 py-4 border-b border-slate-200">
          <div>
            <h2 className="text-xl font-bold text-slate-900">
              Détails de l'Affaire - {project.refGlobal || '-'}
            </h2>
            <div className="flex items-center mt-1 text-sm text-slate-500">
              <span
                className="px-2 py-0.5 rounded-full text-white text-xs font-medium mr-2"
                style={{ backgroundColor: statusColor }}
              >
                {statut}
              </span>
              <span>Créé le {formatDate(convertFirestoreTimestamp(project.createdAt))}</span>
              {isProjectLate(project, delaiRetard) && (
                <span className="ml-2 inline-flex items-center gap-1 text-[11px] font-medium text-red-700 bg-red-50 px-2 py-0.5 rounded-full">
                  <AlertCircle className="h-3 w-3" /> En retard · {getDaysSinceStatus(project)} j sans évolution
                </span>
              )}
              {canSeeFinance && (
                <span className={`ml-2 text-[11px] font-medium px-2 py-0.5 rounded-full ${PAYMENT_STATUS_STYLES[getPaymentStatus(project)]}`}>
                  {PAYMENT_STATUS_LABELS[getPaymentStatus(project)]}
                </span>
              )}
            </div>
          </div>
          <button
            onClick={onClose}
            className="icon-btn" aria-label="Fermer"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Actions rapides */}
        <div className="flex flex-wrap items-center justify-between gap-2 px-4 sm:px-6 py-3 bg-slate-50 border-b border-slate-200">
          <div className="flex flex-wrap gap-2">
            <button
              onClick={openInGoogleMaps}
              disabled={!project.coordonnees?.length}
              className="btn-secondary !min-h-[40px] px-3"
            >
              <MapPin className="h-4 w-4 mr-1.5" />
              Voir sur Google Maps
            </button>
            {canSeeFinance && (
            <button
              onClick={generateReceipt}
              disabled={generatingPdf}
              className="btn-success !min-h-[40px] px-3"
            >
              {generatingPdf ? (
                <>
                  <svg className="animate-spin h-4 w-4 mr-1.5" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"></path>
                  </svg>
                  Génération...
                </>
              ) : (
                <>
                  <FileDown className="h-4 w-4 mr-1.5" />
                  Télécharger Reçu
                </>
              )}
            </button>
            )}
            {!readOnly && availableTransitions.length > 0 && (
              <button
                onClick={() => setShowStatusChange(!showStatusChange)}
                className="btn-primary !min-h-[40px] px-3"
              >
                <History className="h-4 w-4 mr-1.5" />
                Changer le statut
              </button>
            )}
          </div>
          {readOnly ? (
            <span className="text-xs text-slate-500 italic">Consultation seule</span>
          ) : (
            <button
              onClick={handleDelete}
              disabled={loading}
              className="btn-danger-soft !min-h-[40px] px-3"
            >
              <Trash2 className="h-4 w-4 mr-1.5" />
              Supprimer
            </button>
          )}
        </div>

        {/* Status Change Panel */}
        {!readOnly && showStatusChange && availableTransitions.length > 0 && (
          <div className="px-6 py-4 bg-brand-50 border-b">
            <h4 className="text-sm font-medium text-brand-800 mb-3">Changer le statut</h4>
            <div className="flex flex-col sm:flex-row sm:items-start gap-3">
              <select
                value={selectedNextStatus}
                onChange={(e) => setSelectedNextStatus(e.target.value)}
                className="field flex-1"
              >
                <option value="">Sélectionner le nouveau statut...</option>
                {availableTransitions.map(status => (
                  <option key={status} value={status}>{status}</option>
                ))}
              </select>
              <input
                type="text"
                value={statusComment}
                onChange={(e) => setStatusComment(e.target.value)}
                placeholder={selectedNextStatus === 'Défavorable' ? 'Remarques de la commission (obligatoires) *' : 'Commentaire (optionnel)'}
                className={`field flex-1 ${selectedNextStatus === 'Défavorable' && !statusComment.trim() ? 'border-red-300' : ''}`}
                aria-required={selectedNextStatus === 'Défavorable'}
              />
              <button
                onClick={handleStatusChange}
                disabled={!selectedNextStatus || loading || (selectedNextStatus === 'Défavorable' && !statusComment.trim())}
                className="btn-primary"
              >
                Confirmer
              </button>
            </div>
            {error && <p className="mt-2 text-sm text-red-600" role="alert">{error}</p>}
            {selectedNextStatus === 'Défavorable' && (
              <p className="mt-2 text-xs text-red-700">Un avis défavorable doit être accompagné des remarques de la commission ; elles seront transmises à l'architecte et à l'agent responsable.</p>
            )}
            <p className="mt-2 text-xs text-brand-700">
              Transitions possibles depuis "{statut}": {availableTransitions.join(', ')}
            </p>
          </div>
        )}

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-4 sm:p-6 custom-scrollbar">
          {/* Section Historique des statuts */}
          <div className="mb-6">
            <h3 className="text-sm font-semibold text-slate-700 mb-3 uppercase tracking-wide flex items-center">
              <History className="h-4 w-4 mr-2" />
              Historique des Statuts
            </h3>
            <div className="bg-slate-50 rounded-lg p-4">
              <StatusTimeline
                statusHistory={project.statusHistory}
                currentStatus={statut}
              />
            </div>
          </div>

          {/* Section Informations projet */}
          <div className="mb-6">
            <h3 className="text-sm font-semibold text-slate-700 mb-3 uppercase tracking-wide">
              Informations du projet
            </h3>
            <div className="mb-3 bg-slate-50 rounded-lg p-4">
              <p className="text-xs text-slate-500 mb-2">Client(s)</p>
              <div className="space-y-2">
                {clients.filter(c => c.nom).length === 0 && <p className="font-medium text-slate-900">-</p>}
                {clients.filter(c => c.nom).map((c) => (
                  <div key={c.id} className="flex flex-wrap items-center gap-x-3 gap-y-1">
                    <span className={`text-xs px-2 py-0.5 rounded-full ${c.type === 'societe' ? 'bg-indigo-100 text-indigo-800' : 'bg-brand-100 text-brand-800'}`}>
                      {c.type === 'societe' ? 'Société' : 'Personne physique'}
                    </span>
                    <span className="font-medium text-slate-900">{c.nom}</span>
                    {clientIdentifier(c) && <span className="text-sm text-slate-600">{clientIdentifier(c)}</span>}
                    {c.telephone && <span className="text-sm text-slate-500">{c.telephone}</span>}
                  </div>
                ))}
              </div>
              {representant && (
                <div className="mt-3 pt-3 border-t border-slate-200 text-sm">
                  <span className="text-xs text-slate-500 mr-2">Représentant :</span>
                  <span className="font-medium text-slate-900">{representant.nom}</span>
                  {representant.qualite && <span className="text-slate-600"> ({representant.qualite})</span>}
                  {representant.cin && <span className="text-slate-600"> · CIN {representant.cin}</span>}
                  {representant.telephone && <span className="text-slate-500"> · {representant.telephone}</span>}
                </div>
              )}
              {!readOnly && (
                <p className="mt-2 text-xs text-slate-400">Modifiez les clients via le bouton « Modifier » du tableau de bord.</p>
              )}
            </div>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4 bg-slate-50 rounded-lg p-4">
              <EditableField label="Commune" field="commune" value={project.commune} />
              <EditableField label="Province" field="province" value={project.province} />
              <EditableField label="Régime Foncier" field="regimeFoncier" value={project.regimeFoncier} />
              {project.regimeReference && (
                <EditableField label="Référence" field="regimeReference" value={project.regimeReference} />
              )}
              <EditableField label="Nature du Projet" field="natureProjet" value={project.natureProjet} />
              <EditableField label="Agent Responsable" field="agentResponsable" value={project.agentResponsable} />
              <EditableField label="Téléphone" field="telephone" value={project.telephone || project.telIntermediaire} />
              <EditableField label="Date" field="date" value={project.date} type="date" />
              <EditableField label="BET" field="bet" value={project.bet} />
              <EditableField label="TOPO" field="topo" value={project.topo} />
              {canSeeFinance && (project.fraisSupplementaires > 0 || project.fraisSupplementairesNote) && (
                <EditableField label="Motif des frais supplémentaires" field="fraisSupplementairesNote" value={project.fraisSupplementairesNote} />
              )}
            </div>
          </div>

          {/* Section Financier */}
          {canSeeFinance && (
          <div className="mb-6">
            <h3 className="text-sm font-semibold text-slate-700 mb-3 uppercase tracking-wide">
              Informations financières
            </h3>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div className="bg-brand-50 rounded-lg p-4 text-center">
                <p className="text-xs text-brand-700 mb-1">Montant Total</p>
                <p className="text-xl font-bold text-brand-800">{formatNumber(project.montantTotal || 0)} DH</p>
              </div>
              <div className="bg-slate-50 rounded-lg p-4 text-center">
                <p className="text-xs text-slate-600 mb-1">Frais Supp.</p>
                <p className="text-xl font-bold text-slate-800">{formatNumber(project.fraisSupplementaires || 0)} DH</p>
                {project.fraisSupplementairesNote && (
                  <p className="text-[11px] text-slate-500 mt-1 leading-snug" title={project.fraisSupplementairesNote}>{project.fraisSupplementairesNote}</p>
                )}
              </div>
              <div className="bg-green-50 rounded-lg p-4 text-center">
                <p className="text-xs text-green-600 mb-1">Total Avances</p>
                <p className="text-xl font-bold text-green-800">{formatNumber(totalAvances)} DH</p>
              </div>
              <div className={`rounded-lg p-4 text-center ${resteAPayer > 0 ? 'bg-orange-50' : 'bg-green-50'}`}>
                <p className={`text-xs mb-1 ${resteAPayer > 0 ? 'text-orange-600' : 'text-green-600'}`}>Reste à Payer</p>
                <p className={`text-xl font-bold ${resteAPayer > 0 ? 'text-orange-800' : 'text-green-800'}`}>
                  {formatNumber(resteAPayer)} DH
                </p>
              </div>
            </div>
          </div>
          )}

          {/* Section Coordonnées */}
          <div className="mb-6">
            <h3 className="text-sm font-semibold text-slate-700 mb-3 uppercase tracking-wide">
              Coordonnées de la Parcelle ({project.coordonnees?.length || 0} points) - {getZoneName(project.zoneMerchich)}
            </h3>
            {project.coordonnees && project.coordonnees.length > 0 ? (
              <div className="space-y-2">
                {project.coordonnees.map((coord, index) => {
                  const wgs84 = merchichConverter.merchichToWGS84(coord.x, coord.y, project.zoneMerchich);
                  return (
                    <div key={coord.id || index} className="bg-slate-50 rounded-lg p-3">
                      <div className="flex items-center justify-between">
                        <span className="font-medium text-slate-900">{coord.nom || `Point ${index + 1}`}</span>
                        <a
                          href={wgs84 ? `https://maps.google.com/maps?q=${wgs84.latitude},${wgs84.longitude}` : '#'}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-brand-700 hover:text-brand-800 text-xs flex items-center"
                        >
                          <ExternalLink className="h-3 w-3 mr-1" />
                          Voir
                        </a>
                      </div>
                      <div className="grid grid-cols-2 gap-4 mt-2 text-sm">
                        <div>
                          <span className="text-slate-500">Merchich:</span>
                          <br />
                          <span className="font-mono">X: {formatNumber(coord.x)} | Y: {formatNumber(coord.y)}</span>
                        </div>
                        {wgs84 && (
                          <div>
                            <span className="text-slate-500">WGS84:</span>
                            <br />
                            <span className="font-mono">
                              {wgs84.latitude.toFixed(6)}°N | {Math.abs(wgs84.longitude).toFixed(6)}°{wgs84.longitude < 0 ? 'W' : 'E'}
                            </span>
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (
              <p className="text-slate-500 text-sm italic">Aucune coordonnée enregistrée</p>
            )}
          </div>

          {/* Section Avances */}
          {canSeeFinance && (
          <div className="mb-6">
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-sm font-semibold text-slate-700 uppercase tracking-wide">
                Gestion des Avances
              </h3>
              <button
                onClick={() => setShowAddAvance(!showAddAvance)}
                className="btn-success !min-h-[40px] px-3"
              >
                <Plus className="h-4 w-4 mr-1" />
                Ajouter une avance
              </button>
            </div>

            {/* Formulaire nouvelle avance */}
            {showAddAvance && (
              <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-4">
                {error && (
                  <div className="mb-3 p-2 bg-red-50 border border-red-200 rounded text-red-600 text-sm flex items-center">
                    <AlertCircle className="h-4 w-4 mr-2" />
                    {error}
                  </div>
                )}
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                  <div>
                    <label className="block text-xs font-medium text-slate-600 mb-1">Montant (DH) *</label>
                    <input
                      type="number"
                      min="0"
                      value={newAvance.montant}
                      onChange={(e) => setNewAvance({ ...newAvance, montant: e.target.value })}
                      className="field"
                      placeholder="10000"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-slate-600 mb-1">Date</label>
                    <input
                      type="date"
                      value={newAvance.date}
                      onChange={(e) => setNewAvance({ ...newAvance, date: e.target.value })}
                      className="field"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-slate-600 mb-1">Description</label>
                    <input
                      type="text"
                      value={newAvance.description}
                      onChange={(e) => setNewAvance({ ...newAvance, description: e.target.value })}
                      className="field"
                      placeholder="Description (optionnel)"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-slate-600 mb-1">Mode de paiement</label>
                    <select
                      value={newAvance.modePaiement}
                      onChange={(e) => setNewAvance({ ...newAvance, modePaiement: e.target.value })}
                      className="field"
                    >
                      {MODES_PAIEMENT.map(m => <option key={m.value} value={m.value}>{m.label}</option>)}
                    </select>
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-slate-600 mb-1">Référence</label>
                    <input
                      type="text"
                      value={newAvance.reference}
                      onChange={(e) => setNewAvance({ ...newAvance, reference: e.target.value })}
                      className="field"
                      placeholder="N° chèque / virement"
                    />
                  </div>
                  <div className="flex items-end">
                    <label className="flex items-center gap-2 min-h-[44px] cursor-pointer">
                      <input
                        type="checkbox"
                        checked={newAvance.enCaisse}
                        onChange={(e) => setNewAvance({ ...newAvance, enCaisse: e.target.checked })}
                        className="h-4 w-4 rounded border-slate-300 text-brand-600 focus:ring-brand-500"
                      />
                      <span className="text-sm text-slate-700">Encaissé en caisse</span>
                    </label>
                  </div>
                </div>
                <div className="flex justify-end space-x-2 mt-3">
                  <button
                    onClick={() => {
                      setShowAddAvance(false);
                      setNewAvance({ montant: '', date: '', description: '', modePaiement: 'espece', enCaisse: true, reference: '' });
                      setError('');
                    }}
                    className="px-3 py-1.5 text-sm text-slate-600 hover:bg-slate-100 rounded-xl transition-colors"
                  >
                    Annuler
                  </button>
                  <button
                    onClick={handleAddAvance}
                    disabled={loading}
                    className="btn-success"
                  >
                    {loading ? 'Ajout...' : 'Ajouter'}
                  </button>
                </div>
              </div>
            )}

            {/* Liste des avances */}
            {project.avances && project.avances.length > 0 ? (
              <div className="border border-slate-200 rounded-lg divide-y">
                {[...project.avances].reverse().map((avance, index) => (
                  <div key={avance.id || index} className="flex items-center justify-between px-4 py-3 hover:bg-slate-50">
                    <div>
                      <div className="flex items-center">
                        <span className="font-medium text-slate-900">{formatNumber(avance.montant)} DH</span>
                        <span className="mx-2 text-slate-300">|</span>
                        <span className="text-sm text-slate-500">{formatDate(avance.date)}{avance.modePaiement ? ` · ${MODES_PAIEMENT.find(m => m.value === avance.modePaiement)?.label || avance.modePaiement}` : ''}{avance.enCaisse ? ' · caisse' : ''}{avance.reference ? ` · ${avance.reference}` : ''}</span>
                      </div>
                      {avance.description && (
                        <p className="text-sm text-slate-500 mt-0.5">{avance.description}</p>
                      )}
                    </div>
                    <button
                      onClick={() => handleDeleteAvance(avance.id)}
                      disabled={loading}
                      className="icon-btn text-red-500 hover:bg-red-50 hover:text-red-700" aria-label="Supprimer"
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-slate-500 text-sm italic">Aucune avance enregistrée</p>
            )}

            {/* Résumé financier */}
            <div className="mt-4 bg-brand-50 border border-brand-200 rounded-lg p-4">
              <div className="grid grid-cols-3 gap-4 text-center">
                <div>
                  <p className="text-xs text-brand-700">Montant projet</p>
                  <p className="text-lg font-bold text-brand-800">
                    {formatNumber((project.montantTotal || 0) + (project.fraisSupplementaires || 0))} DH
                  </p>
                </div>
                <div>
                  <p className="text-xs text-brand-700">Total avances</p>
                  <p className="text-lg font-bold text-brand-800">{formatNumber(totalAvances)} DH</p>
                </div>
                <div>
                  <p className={`text-xs ${resteAPayer > 0 ? 'text-orange-600' : 'text-green-600'}`}>Reste à payer</p>
                  <p className={`text-lg font-bold ${resteAPayer > 0 ? 'text-orange-800' : 'text-green-800'}`}>
                    {formatNumber(resteAPayer)} DH
                  </p>
                </div>
              </div>
            </div>
          </div>
          )}

          {/* Notes & échanges */}
          {author?.uid && (
            <ProjectNotes project={project} author={author} agents={agents} canDeleteAll={canManage} />
          )}

          {/* Section Observations */}
          <div className="mb-6">
            <h3 className="text-sm font-semibold text-slate-700 mb-3 uppercase tracking-wide">
              Observations
            </h3>
            <div className="bg-slate-50 rounded-lg p-4">
              <EditableField label="" field="observations" value={project.observations} />
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-1 px-4 sm:px-6 py-3 border-t border-slate-200 bg-slate-50 text-xs sm:text-sm text-slate-500">
          <span>
            Dernière modification: {formatDateTime(convertFirestoreTimestamp(project.updatedAt))}
          </span>
          <span>
            Réf: {project.refGlobal || '-'} | Dossier: {project.numeroDossier || '-'}
          </span>
        </div>
      </div>
    </div>
  );
};

export default ProjectDetails;
